import React, { useRef, useEffect, useState } from "react";

interface NumberInputProps {
    value: number | string;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    width?: number;  // Fixed width in pixels
    height?: number; // Fixed height in pixels
    style?: React.CSSProperties;
    className?: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
}

export const NumberInput: React.FC<NumberInputProps> = ({
    value,
    onChange,
    min,
    max,
    width = 80,  // Default width: 80px
    height = 36, // Default height: 36px
    style,
    className,
    placeholder,
    disabled,
    required = false,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isAltPressed, setIsAltPressed] = useState(false);
    const [isShiftPressed, setIsShiftPressed] = useState(false);

    // Tooltip text
    const tooltip =
        "Use up/down arrows or buttons to adjust.\nHold Alt for 0.1 increments\nHold Shift for 10 increments.";

    // Track modifier keys
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.altKey) setIsAltPressed(true);
            if (e.shiftKey) setIsShiftPressed(true);
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            if (!e.altKey) setIsAltPressed(false);
            if (!e.shiftKey) setIsShiftPressed(false);
        };

        // Reset modifier keys when window loses focus or visibility changes
        const handleWindowBlur = () => {
            setIsAltPressed(false);
            setIsShiftPressed(false);
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                setIsAltPressed(false);
                setIsShiftPressed(false);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);
        window.addEventListener("blur", handleWindowBlur);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
            window.removeEventListener("blur", handleWindowBlur);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            // Reset states on unmount to prevent stuck states
            setIsAltPressed(false);
            setIsShiftPressed(false);
        };
    }, []);

    // Calculate increment based on modifier keys
    const getIncrement = () => {
        if (isAltPressed) return 0.1;
        if (isShiftPressed) return 10;
        return 1;
    };

    // Handle increment/decrement
    const handleIncrement = (direction: "up" | "down") => {
        if (disabled) return;
        
        const currentValue = typeof value === "string" ? parseFloat(value) : value;
        if (isNaN(currentValue)) {
            // If current value is NaN, start from 0
            onChange(direction === "up" ? getIncrement() : -getIncrement());
            return;
        }
        
        const increment = getIncrement();
        let newValue = direction === "up" 
            ? currentValue + increment 
            : currentValue - increment;
            
        // Apply min/max constraints
        if (min !== undefined) newValue = Math.max(min, newValue);
        if (max !== undefined) newValue = Math.min(max, newValue);
        
        // Round to avoid floating point precision issues
        // For 0.1 increments, round to 1 decimal place
        // For 1 increments, round to nearest integer
        // For 10 increments, round to nearest integer
        let roundedValue: number;
        if (increment === 0.1) {
            roundedValue = Math.round(newValue * 10) / 10;
        } else {
            roundedValue = Math.round(newValue);
        }
        
        onChange(roundedValue);
    };

    // Handle keydown for arrow keys
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault();
            handleIncrement(e.key === "ArrowUp" ? "up" : "down");
        }
    };

    // Handle direct input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        
        // Allow empty string temporarily (user might be clearing to type new value)
        if (val === "") {
            onChange(0);
            return;
        }
        
        const numVal = parseFloat(val);
        if (!isNaN(numVal)) {
            // Don't enforce min/max on direct typing, let user finish typing
            onChange(numVal);
        }
    };

    // Handle blur to enforce min/max constraints
    const handleBlur = () => {
        const currentValue = typeof value === "string" ? parseFloat(value) : value;
        if (!isNaN(currentValue)) {
            let constrainedValue = currentValue;
            if (min !== undefined) constrainedValue = Math.max(min, constrainedValue);
            if (max !== undefined) constrainedValue = Math.min(max, constrainedValue);
            if (constrainedValue !== currentValue) {
                onChange(constrainedValue);
            }
        }
    };

    // Fixed container style
    const containerStyle: React.CSSProperties = {
        position: "relative",
        display: "inline-block",
        width: `${width}px`,
        height: `${height}px`,
        flexShrink: 0, // Prevent shrinking in flex containers
        ...style,
    };

    // Input style - fixed dimensions
    const inputStyle: React.CSSProperties = {
        width: "100%",
        height: "100%",
        paddingLeft: "8px",
        paddingRight: "28px", // Space for spinner buttons
        paddingTop: "0",
        paddingBottom: "0",
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        fontSize: "14px",
        lineHeight: `${height - 2}px`, // Account for border
        backgroundColor: disabled ? "#f9fafb" : "white",
        color: disabled ? "#9ca3af" : "#111827",
        outline: "none",
        transition: "border-color 0.15s ease-in-out",
        appearance: "textfield", // Hide default spinners
        MozAppearance: "textfield",
        WebkitAppearance: "none",
        boxSizing: "border-box",
    };

    // Spinner container - positioned inside the input
    const spinnerContainerStyle: React.CSSProperties = {
        position: "absolute",
        right: "1px",
        top: "1px",
        bottom: "1px",
        width: "24px",
        display: "flex",
        flexDirection: "column",
        borderLeft: "1px solid #e5e7eb",
        borderRadius: "0 5px 5px 0",
        overflow: "hidden",
    };

    // Spinner button base style
    const spinnerButtonStyle: React.CSSProperties = {
        flex: 1,
        border: "none",
        background: disabled ? "#f9fafb" : "#f9fafb",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "8px",
        color: disabled ? "#d1d5db" : "#6b7280",
        padding: 0,
        transition: "background-color 0.15s ease-in-out",
        userSelect: "none",
    };

    const topButtonStyle: React.CSSProperties = {
        ...spinnerButtonStyle,
        borderBottom: "1px solid #e5e7eb",
    };

    const bottomButtonStyle: React.CSSProperties = {
        ...spinnerButtonStyle,
    };

    // Focus style for the container
    const [isFocused, setIsFocused] = useState(false);

    return (
        <div style={containerStyle} className={className} title={tooltip}>
            <style>
                {`
                    /* Hide spinner buttons for Webkit browsers */
                    input[type=number]::-webkit-inner-spin-button,
                    input[type=number]::-webkit-outer-spin-button {
                        -webkit-appearance: none;
                        margin: 0;
                    }
                    
                    /* Focus styles */
                    input[type=number]:focus {
                        border-color: #3b82f6;
                        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                    }
                `}
            </style>
            <input
                ref={inputRef}
                type="number"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={(e) => {
                    handleBlur();
                    setIsFocused(false);
                }}
                onFocus={() => {
                    setIsFocused(true);
                    // Reset modifier states on focus to prevent stuck states
                    setIsAltPressed(false);
                    setIsShiftPressed(false);
                }}
                style={{
                    ...inputStyle,
                    borderColor: isFocused ? "#3b82f6" : "#d1d5db",
                }}
                placeholder={placeholder}
                disabled={disabled}
                required={required}
            />
            <div style={spinnerContainerStyle}>
                <button
                    type="button"
                    style={topButtonStyle}
                    onClick={() => handleIncrement("up")}
                    onMouseEnter={(e) => !disabled && (e.currentTarget.style.backgroundColor = "#e5e7eb")}
                    onMouseLeave={(e) => !disabled && (e.currentTarget.style.backgroundColor = "#f9fafb")}
                    onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
                    disabled={disabled}
                    tabIndex={-1}
                    aria-label="Increment value"
                >
                    <svg width="8" height="5" viewBox="0 0 8 5" fill="currentColor">
                        <path d="M4 0L7.5 5H0.5L4 0Z" />
                    </svg>
                </button>
                <button
                    type="button"
                    style={bottomButtonStyle}
                    onClick={() => handleIncrement("down")}
                    onMouseEnter={(e) => !disabled && (e.currentTarget.style.backgroundColor = "#e5e7eb")}
                    onMouseLeave={(e) => !disabled && (e.currentTarget.style.backgroundColor = "#f9fafb")}
                    onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
                    disabled={disabled}
                    tabIndex={-1}
                    aria-label="Decrement value"
                >
                    <svg width="8" height="5" viewBox="0 0 8 5" fill="currentColor">
                        <path d="M4 5L0.5 0H7.5L4 5Z" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default NumberInput; 